import { FC, useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import usePlace from 'providers/Place/hooks';

type AnalyticsScriptsType = {};

/**
 * Scripts must be injected imperatively. React intentionally creates
 * `<script>` elements rendered from JSX in a way that prevents the browser
 * from executing them, so `<script dangerouslySetInnerHTML>` and
 * `<script src>` in JSX silently do nothing.
 */
const injectScript = (id: string, { src, code }: { src?: string; code?: string }) => {
	if (document.getElementById(id)) return;

	const script = document.createElement('script');
	script.id = id;

	if (src) {
		script.async = true;
		script.src = src;
	} else if (code) {
		script.text = code;
	}

	document.head.appendChild(script);
};

const consentModeCode = (granted: boolean) => {
	const state = granted ? 'granted' : 'denied';

	return `
		window.dataLayer = window.dataLayer || [];
		function gtag(){window.dataLayer.push(arguments);}
		gtag('consent', 'default', {
			'ad_storage': '${state}',
			'ad_user_data': '${state}',
			'ad_personalization': '${state}',
			'analytics_storage': '${state}',
			'wait_for_update': 500,
		});
		window.acceptGoogleAnalyticsConsentCookies = () => {
			gtag('consent', 'update', {
				'ad_user_data': 'granted',
				'ad_personalization': 'granted',
				'ad_storage': 'granted',
				'analytics_storage': 'granted'
			});
		};
	`;
};

const gaConfigCode = (gaId: string) => `
	window.dataLayer = window.dataLayer || [];
	function gtag(){window.dataLayer.push(arguments);}
	window.gtag = window.gtag || gtag;
	gtag('js', new Date());
	gtag('config', '${gaId}');
`;

const gtmLoaderCode = (gtmId: string) => `
	(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
	new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
	j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
	'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
	})(window,document,'script','dataLayer','${gtmId}');
`;

const fbPixelCode = (pixelId: string) => `
	!function(f,b,e,v,n,t,s)
	{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
	n.callMethod.apply(n,arguments):n.queue.push(arguments)};
	if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version="2.0";
	n.queue=[];t=b.createElement(e);t.async=!0;
	t.src=v;s=b.getElementsByTagName(e)[0];
	s.parentNode.insertBefore(t,s)}(window, document,"script",
	"https://connect.facebook.net/en_US/fbevents.js");
	fbq("init", "${pixelId}");
	fbq("track", "PageView");
`;

const AnalyticsScripts: FC<AnalyticsScriptsType> = () => {
	const { place } = usePlace();
	const [searchParams] = useSearchParams();
	const isGoogleInitializedRef = useRef(false);

	const analytics = place?.marketing?.analytics;
	const isCookiesGranted = searchParams.get('cookies-granted') === 'true';

	useEffect(() => {
		window.isCookiesGranted = isCookiesGranted;
		window.analytics = {
			isGa: !!analytics?.ga,
			isGtm: !!analytics?.gtm,
			isFbPixel: !!analytics?.fbPixel,
		};
	}, [isCookiesGranted, analytics]);

	useEffect(() => {
		const { ga, gtm, fbPixel, googleConsentModeAdvanced } = analytics ?? {};

		// Without consent nothing is loaded. The only exception is Google's
		// advanced consent mode, which by design loads tags before consent and
		// sends cookieless pings while the consent state is "denied".
		const canLoadGoogle = isCookiesGranted || !!googleConsentModeAdvanced;

		if ((ga || gtm) && canLoadGoogle) {
			if (!isGoogleInitializedRef.current) {
				// Consent default must be registered before gtag.js / gtm.js load.
				injectScript('analytics-consent-mode', { code: consentModeCode(isCookiesGranted) });
				isGoogleInitializedRef.current = true;
			} else if (isCookiesGranted) {
				window.acceptGoogleAnalyticsConsentCookies?.();
			}

			if (ga) {
				injectScript('analytics-ga-lib', {
					src: `https://www.googletagmanager.com/gtag/js?id=${ga}`,
				});
				injectScript('analytics-ga', { code: gaConfigCode(ga) });
			}

			if (gtm) {
				injectScript('analytics-gtm', { code: gtmLoaderCode(gtm) });
			}
		}

		if (fbPixel && isCookiesGranted) {
			injectScript('analytics-fbp', { code: fbPixelCode(fbPixel) });
		}
	}, [
		analytics?.ga,
		analytics?.gtm,
		analytics?.fbPixel,
		analytics?.googleConsentModeAdvanced,
		isCookiesGranted,
	]);

	return null;
};

export default AnalyticsScripts;
